// HoroscopePage.jsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  InputAdornment,
  TextField,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { HOROSCOPE_ENDPOINT, HOROSCOPE_CSV_UPLOAD_ENDPOINT } from "../../config/urls";

// ── Zodiac meta data ────────────────────────────────────────────────────────
const ZODIAC_META = {
  Aries:       { emoji: "♈", color: "#CF1322", bg: "#FFF1F0" },
  Taurus:      { emoji: "♉", color: "#389E0D", bg: "#F6FFED" },
  Gemini:      { emoji: "♊", color: "#096DD9", bg: "#E6F7FF" },
  Cancer:      { emoji: "♋", color: "#1D39C4", bg: "#F0F5FF" },
  Leo:         { emoji: "♌", color: "#D48806", bg: "#FFFBE6" },
  Virgo:       { emoji: "♍", color: "#531DAB", bg: "#F9F0FF" },
  Libra:       { emoji: "♎", color: "#C41D7F", bg: "#FFF0F6" },
  Scorpio:     { emoji: "♏", color: "#AD4E00", bg: "#FFF2E8" },
  Sagittarius: { emoji: "♐", color: "#3F6600", bg: "#FCFFE6" },
  Capricorn:   { emoji: "♑", color: "#006D75", bg: "#F0FFF4" },
  Aquarius:    { emoji: "♒", color: "#0891B2", bg: "#E0F7FA" },
  Pisces:      { emoji: "♓", color: "#7C3AED", bg: "#F5F0FF" },
};

// ── Generate options from API response dynamically ──────────────────────────
const generateOptions = (data, field) => {
  if (!data || !Array.isArray(data)) return [];
  const uniqueValues = [...new Set(data.map(item => item[field]))].filter(Boolean);
  return uniqueValues.sort().map(value => ({ label: value, value }));
};

// ── Zodiac Sign Cell ─────────────────────────────────────────────────────────
const ZodiacCell = ({ sign }) => {
  const meta = ZODIAC_META[sign] || { emoji: "⭐", color: "#08245C", bg: "#F0F5FF" };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 32, height: 32, borderRadius: "8px",
          background: meta.bg, display: "flex",
          alignItems: "center", justifyContent: "center",
          fontSize: "17px", flexShrink: 0,
        }}
      >
        {meta.emoji}
      </Box>
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: meta.color }}>
        {sign}
      </Typography>
    </Box>
  );
};

const currentDate = new Date();
const CURRENT_YEAR = currentDate.getFullYear();
const CURRENT_MONTH = currentDate.toLocaleString("default", { month: "long" });

const ALL_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// ── Upload Modal Component ──────────────────────────────────────────────────
const UploadModal = ({
  open,
  onClose,
  onUpload,
  onDownloadSample,
  uploading,
  importResults,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  const handleDownload = () => {
    onDownloadSample();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "20px",
          fontWeight: 700,
          color: "#08245C",
          pb: 1,
        }}
      >
        Upload Horoscope
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="text"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleDownload}
            sx={{
              color: "#1B76D1",
              textTransform: "none",
              ...typography.bodyLarge,
              fontWeight: 600,
              p: 0,
              mb: 2,
              "&:hover": { background: "transparent", textDecoration: "underline" }
            }}
          >
            Click here to download sample file
          </Button>
          
          <Typography sx={{ ...typography.bodyLarge, color: "#6B7280", mb: 2 }}>
            Max. 10,000 rows per upload
          </Typography>

          <Button
            variant="outlined"
            component="label"
            startIcon={<FileUploadOutlinedIcon />}
            sx={{
              width: "100%",
              height: 100,
              border: "2px dashed #E5E7EB",
              borderRadius: "8px",
              textTransform: "none",
              ...typography.bodyLarge,
              color: "#6B7280",
              "&:hover": { borderColor: "#1B76D1", background: "#F8FAFF" }
            }}
          >
            {selectedFile ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <InsertDriveFileIcon sx={{ color: "#1B76D1" }} />
                <Typography>{selectedFile.name}</Typography>
              </Stack>
            ) : (
              "Select File"
            )}
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileSelect}
            />
          </Button>
        </Box>

        {importResults && (
          <Box sx={{ mt: 2 }}>
            {(importResults.created > 0 || importResults.updated > 0) && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: "#F0FFF4",
                  border: "1px solid #2DC77A",
                  borderRadius: "8px",
                }}
              >
                <Typography sx={{ fontWeight: 600, color: "#2DC77A" }}>
                  ✓ {importResults.message || "Upload successful"}
                </Typography>
                <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                  <Typography>
                    Created: <strong>{importResults.created || 0}</strong>
                  </Typography>
                  <Typography>
                    Updated: <strong>{importResults.updated || 0}</strong>
                  </Typography>
                  <Typography>
                    Total: <strong>{importResults.total || 0}</strong>
                  </Typography>
                </Stack>
              </Paper>
            )}

            {importResults.row_errors?.length > 0 && (
              <>
                <Typography sx={{ color: "#F04438", fontWeight: 600, mb: 1 }}>
                  {importResults.row_errors.length} error(s) found:
                </Typography>
                <List
                  sx={{
                    maxHeight: 200,
                    overflow: "auto",
                    bgcolor: "#FEF2F2",
                    borderRadius: "8px",
                    p: 1,
                  }}
                >
                  {importResults.row_errors.map((err, idx) => (
                    <ListItem
                      key={idx}
                      sx={{
                        py: 0.5,
                        borderBottom:
                          idx < importResults.row_errors.length - 1
                            ? "1px solid #FECACA"
                            : "none",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: "13px" }}>
                            <strong>Line {err.line}:</strong> {err.msg}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {importResults.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {importResults.error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            ...typography.bodyLarge,
            fontWeight: 600,
            height: 40,
            borderColor: "#E5E7EB",
            color: "#1F2937"
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || uploading}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            ...typography.bodyLarge,
            fontWeight: 600,
            height: 40,
            background: "#1B76D1",
            "&:hover": { background: "#1560b0" },
            "&.Mui-disabled": { background: "#9CA3AF" }
          }}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const HoroscopePage = () => {
  const [horoscopes, setHoroscopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Import states
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // Filter states (Updated with defaults)
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState(CURRENT_MONTH); // Default to current month
  const [yearFilter, setYearFilter] = useState(String(CURRENT_YEAR)); // Default to current year
  const [signFilter, setSignFilter] = useState("");
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { getValidToken } = useAuth();

  // ── API Functions ──────────────────────────────────────────────────────────
  const fetchHoroscopes = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      
      // Build query params
      const params = new URLSearchParams({
        page: page + 1,
        page_size: rowsPerPage,
      });
      
      if (monthFilter) params.append("month", monthFilter);
      if (yearFilter) params.append("year", yearFilter);
      if (signFilter) params.append("zodiac_sign", signFilter);
      if (search) params.append("search", search);
      
      const response = await fetch(
        `${HOROSCOPE_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) throw new Error("Failed to fetch horoscope data");
      
      const data = await response.json();
      setHoroscopes(data.results || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      console.error("Error fetching horoscopes:", err);
      setError(err.message);
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch on initial load and when filters change
  useEffect(() => {
    fetchHoroscopes();
  }, [page, rowsPerPage, monthFilter, yearFilter, signFilter, search]);

  // ── Import Function ───────────────────────────────────────────────────────
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setSnackbar({
        open: true,
        message: "Please select a CSV file",
        severity: "error"
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: "File size exceeds 5MB limit",
        severity: "error"
      });
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(HOROSCOPE_CSV_UPLOAD_ENDPOINT, {
        method: "POST",
        headers: {
          "X-Tenant-Id": tenant,
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle validation errors from backend
        setImportResults({
          error: result.error,
          row_errors: result.row_errors || [],
          total_rows: result.total_rows || 0,
          missing_columns: result.missing_columns,
          required_columns: result.required_columns,
        });
      } else {
        // Success
        setImportResults(result);
        // Refresh the list after successful import
        fetchHoroscopes();
      }
      
      setImportDialogOpen(true);
    } catch (err) {
      console.error("Error uploading file:", err);
      setSnackbar({
        open: true,
        message: `Upload failed: ${err.message}`,
        severity: "error"
      });
    } finally {
      setImporting(false);
    }
  };

  // ── Download Sample CSV Function ──────────────────────────────────────────
  const handleDownloadSample = () => {
    const sampleCSVContent = [
      "year,month,zodiac_sign,horoscope_text",
      `2026,April,Aries,"Your dynamic energy will open new doors this month. Stay focused on your primary goals and avoid unnecessary distractions."`,
      `2026,April,Taurus,"Financial stability is on the horizon. Take time to review your investments and trust your practical instincts."`,
      `2026,April,Gemini,"Communication is your superpower right now. Reach out to old friends and network actively in your professional circle."`,
      `2026,April,Cancer,"Focus on your home and family life. A peaceful environment will give you the emotional grounding you need for work."`,
      `2026,April,Leo,"Your natural leadership skills will be recognized. Step up to the plate on that new project, but remember to delegate."`,
      `2026,April,Virgo,"Attention to detail will save you from a minor setback. Keep your workspace organized and prioritize your health."`,
      `2026,April,Libra,"Balance is key this month. Don't let work consume your personal life. Make time for art, music, and relaxation."`,
      `2026,April,Scorpio,"Your intuition is highly tuned. Trust your gut feelings regarding a new partnership or business opportunity."`,
      `2026,April,Sagittarius,"Adventure is calling! Even if you can't travel far, find ways to explore new subjects or philosophies locally."`,
      `2026,April,Capricorn,"Hard work is about to pay off. Stay disciplined, but don't forget to celebrate your small victories along the way."`,
      `2026,April,Aquarius,"Innovative ideas will flow effortlessly. Share your vision with your team, as collaboration will bring it to life."`,
      `2026,April,Pisces,"Take time for spiritual and emotional renewal. Listen to your dreams and allow your creativity to guide your decisions."`
    ].join("\n");

    const blob = new Blob(["\uFEFF" + sampleCSVContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "horoscope_upload_template.csv");
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: "Sample template downloaded successfully",
      severity: "success"
    });
  };

  // ── Export Function ───────────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      setSnackbar({
        open: true,
        message: "Preparing export...",
        severity: "info"
      });

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      
      // Build query params for export
      const params = new URLSearchParams({
        page_size: 10000,
      });
      
      if (monthFilter) params.append("month", monthFilter);
      if (yearFilter) params.append("year", yearFilter);
      if (signFilter) params.append("zodiac_sign", signFilter);
      if (search) params.append("search", search);
      
      const response = await fetch(
        `${HOROSCOPE_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) throw new Error("Failed to fetch data for export");
      
      const data = await response.json();
      const exportData = data.results || horoscopes;
      
      if (exportData.length === 0) {
        setSnackbar({
          open: true,
          message: "No data to export",
          severity: "warning"
        });
        return;
      }

      // Define CSV headers matching backend expectations
      const headers = [
        "zodiac_sign",
        "month",
        "year",
        "horoscope_text"
      ];

      // Transform data to CSV rows
      const rows = exportData.map((row) => [
        row.zodiac_sign || "",
        row.month || "",
        row.year || "",
        // Escape quotes and handle multiline text
        row.horoscope_text ? `"${row.horoscope_text.replace(/"/g, '""')}"` : "",
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.join(","))
      ].join("\n");

      // Create and download CSV file
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with current date and filters
      const date = new Date().toISOString().split("T")[0];
      const monthSuffix = monthFilter ? `_${monthFilter}` : "";
      const yearSuffix = yearFilter ? `_${yearFilter}` : "";
      link.setAttribute("download", `horoscope_${date}${monthSuffix}${yearSuffix}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `Exported ${exportData.length} records successfully`,
        severity: "success"
      });
    } catch (err) {
      console.error("Error exporting data:", err);
      setSnackbar({
        open: true,
        message: `Export failed: ${err.message}`,
        severity: "error"
      });
    }
  };

  // ── Generate filter options from data ─────────────────────────────────────
  const monthOptions = [
    { label: "All Months", value: "" },
    ...ALL_MONTHS.map(m => ({ label: m, value: m }))
  ];

  const yearOptions = [
    { label: "All Years", value: "" },
    { label: String(CURRENT_YEAR - 1), value: String(CURRENT_YEAR - 1) },
    { label: String(CURRENT_YEAR), value: String(CURRENT_YEAR) },
    { label: String(CURRENT_YEAR + 1), value: String(CURRENT_YEAR + 1) }
  ];

  const signOptions = [
    { label: "All Signs", value: "" },
    ...Object.keys(ZODIAC_META).map(sign => ({ label: sign, value: sign }))
  ];

  const hasActiveFilters = 
    monthFilter !== CURRENT_MONTH || 
    yearFilter !== String(CURRENT_YEAR) || 
    signFilter !== "" || 
    search !== "";

  // ── Column definitions ───────────────────────────────────────────────────
  const columns = [
    {
      id: "year",
      label: "Year",
      minWidth: 80,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#08245C", fontFamily: "DM Mono, monospace" }}>
          {row.year || "—"}
        </Typography>
      ),
    },
    {
      id: "month",
      label: "Month",
      minWidth: 110,
      render: (row) => (
        <Chip
          label={row.month || "—"}
          size="small"
          sx={{
            background: "#E8F1FB", color: "#1B76D1",
            fontWeight: 700, fontSize: "12px",
            borderRadius: "20px", height: 24,
          }}
        />
      ),
    },
    {
      id: "zodiac_sign",
      label: "Zodiac Sign",
      minWidth: 150,
      render: (row) => <ZodiacCell sign={row.zodiac_sign} />,
    },
    {
      id: "horoscope_text",
      label: "Horoscope",
      render: (row) => (
        <Typography
          sx={{
            ...typography.bodyLarge, color: "#1F2937",
            lineHeight: 1.6, whiteSpace: "normal",
          }}
        >
          {row.horoscope_text || "—"}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#08245C", fontFamily: "DM Sans, sans-serif" }}>
            Horoscope
          </Typography>
          <Typography sx={{ ...typography.bodyLarge, color: "#6B7280", mt: 0.5 }}>
            Monthly zodiac-wise horoscope data
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          {/* <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchHoroscopes}
            disabled={loading}
            sx={{
              borderRadius: "8px", textTransform: "none",
              ...typography.bodyLarge, fontWeight: 600, height: 40,
              borderColor: "#E5E7EB", color: "#1F2937",
            }}
          >
            Refresh
          </Button> */}
          
          {/* <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExport}
            sx={{
              borderRadius: "8px", textTransform: "none",
              ...typography.bodyLarge, fontWeight: 600, height: 40,
              borderColor: "#E5E7EB", color: "#1F2937",
            }}
          >
            Export CSV
          </Button> */}
          <Button
            variant="contained"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => setUploadModalOpen(true)}
            sx={{
              background: "#1B76D1", borderRadius: "8px", textTransform: "none",
              ...typography.bodyLarge, fontWeight: 600, height: 40, minWidth: 120,
              "&:hover": { background: "#1560b0" },
            }}
          >
            Upload CSV
          </Button>
        </Stack>
      </Box>

      {/* ── Filters ── */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2.5, flexWrap: "wrap" }}>
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search horoscope text..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
              </InputAdornment>
            ),
            sx: { borderRadius: "8px", height: 40, ...typography.bodyLarge, minWidth: 240 },
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />

        {/* Month filter */}
        <AppDropdown
          menuItems={monthOptions}
          selectedValue={monthFilter}
          onMenuSelect={(v) => { setMonthFilter(v); setPage(0); }}
        >
          {monthFilter || "All Months"}
        </AppDropdown>

        {/* Year filter */}
        <AppDropdown
          menuItems={yearOptions}
          selectedValue={yearFilter}
          onMenuSelect={(v) => { setYearFilter(v); setPage(0); }}
        >
          {yearFilter || "All Years"}
        </AppDropdown>

        {/* Sign filter */}
        <AppDropdown
          menuItems={signOptions}
          selectedValue={signFilter}
          onMenuSelect={(v) => {
            setSignFilter(v);
            setPage(0);
          }}
          endIcon={<KeyboardArrowDownOutlinedIcon />}
        >
          {signFilter || "All Signs"}
        </AppDropdown>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={() => { 
              setMonthFilter(CURRENT_MONTH);
              setYearFilter(String(CURRENT_YEAR));
              setSignFilter(""); 
              setSearch(""); 
              setPage(0); 
            }}
            sx={{ ...typography.bodyLarge, color: "#6B7280", textTransform: "none", height: 40 }}
          >
            Clear filters
          </Button>
        )}
      </Stack>

      {/* ── Table ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <Typography>Loading horoscope data...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <AppTable
          columns={columns}
          rows={horoscopes}
          rowKey="id"
          emptyMessage="No horoscope data found"
          maxHeight={600}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          bodyCellSx={{ whiteSpace: "normal" }}
        />
      )}

      {/* ── Upload Modal ── */}
      <UploadModal
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setImportResults(null);
        }}
        onUpload={handleFileUpload}
        onDownloadSample={handleDownloadSample}
        uploading={importing}
        importResults={importResults}
      />

      {/* ── Snackbar for notifications ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HoroscopePage;